import { Loading, useDataMutation, useDataQuery } from "@lyfegen-platform/react-common";
import { byDesc, sortByComparisons } from "@lyfegen-platform/utils";
import { Box, Card, Typography } from "@material-ui/core";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import DropDownMultipleSelection from "../../../../components/drop-down/DropDownMultipleSelection";
import NotFound from "../../../../components/NotFound";
import { Role } from "../../../../models/identity/role";
import { UserOrg } from "../../../../store/layout";
import { userPersonalInfoState } from "../../../../store/user";
import isJsonString from "../../../../utils/is-json-string";
import { SelectableValue } from "../../../simulator/details/utils/forms";
import {
	GET_ALL_USERS_FROM_ORGANIZATION,
	GET_ORGANIZATION_AGREEMENT_TAGS,
	GET_ORGANIZATION_SETTINGS_FILTERS,
	GET_USER_DOMAINS_TREE,
	UPDATE_ORGANIZATION_SETTINGS_FILTER,
} from "../queries";
import { GetAllUsersFromOrganizationQuery,  GetUserDomainsTreeQuery } from "../types/queries.generated";
import useOrganizationStyles from "../use-organization-styles";
import OrganizationActionSettings from "./OrganizationActionSettings";
import { sortOrganizationDomains, sortOrganizationDomainsLabel } from "../../../simulator/details/utils/sort-domains";
import { useModelsQuery } from "apps/platform-client/client/src/hooks/useModelsModule";

export interface UserData {
	id: string;
	firstName: string;
	lastName: string;
}

const useGetOrganizationAgreementTags = (orgId: string | null) => {
	return useDataQuery(GET_ORGANIZATION_AGREEMENT_TAGS, {
		variables: { id: orgId },
	});
};

const useGetOrganizationUsers = () => {
	return useModelsQuery(GET_ALL_USERS_FROM_ORGANIZATION);
};

const useGetUserDomainsTree = () => {
	return useModelsQuery<GetUserDomainsTreeQuery>(GET_USER_DOMAINS_TREE, { fetchPolicy: "no-cache" });
};

function flattenTree(nodes: GetUserDomainsTreeQuery["GetUserDomainsTree"]) {
	const result: { id: any; name: any; level: any }[] = [];

	function traverse(nodeList: GetUserDomainsTreeQuery["GetUserDomainsTree"]) {
		for (const node of nodeList) {
			result.push({
				id: node.id,
				name: node.name,
				level: node.level,
			});

			if (node.children && node.children.length > 0) {
				traverse(node.children as GetUserDomainsTreeQuery["GetUserDomainsTree"]);
			}
		}
	}

	traverse(nodes);
	return result;
}

export const UsersTab = (props: { userOrg: UserOrg; canAccessDomains: boolean }) => {
	const { t } = useTranslation();
	const [selectedUsers, setSelectedUsers] = useState<SelectableValue[]>([]);
	const [updateOrgSettingsFilter] = useDataMutation(UPDATE_ORGANIZATION_SETTINGS_FILTER);
	const [selectedTags, setSelectedTags] = useState<SelectableValue[]>([]);
	const [selectedDomains, setSelectedDomains] = useState<Array<SelectableValue>>([]);
	const user = useRecoilValue(userPersonalInfoState);
	const { data, loading } = useDataQuery<any>(GET_ORGANIZATION_SETTINGS_FILTERS, { fetchPolicy: "no-cache" });
	const domainFromRedirect = window.sessionStorage.getItem("domain");
	const { data: dataTags, loading: loadingTags, error: errorTags } = useGetOrganizationAgreementTags(props.userOrg.id);
	const { data: dataUsers, loading: loadingUsers, error: errorUsers } = useGetOrganizationUsers();
	const { data: dataDomainsTree, loading: loadingDomains, error: errorDomains } = useGetUserDomainsTree();
	const flattenDomains = dataDomainsTree?.GetUserDomainsTree ? flattenTree(dataDomainsTree.GetUserDomainsTree) : [];

	useEffect(() => {
		if (data) {
			const savedFilters = isJsonString(data.filters) ? JSON.parse(data.filters || "{}") : {};
			setSelectedUsers(savedFilters?.usernames || []);
			setSelectedTags(savedFilters?.tags || []);
			setSelectedDomains(sortOrganizationDomainsLabel(savedFilters?.domains || []));
		}
	}, [data?.filters]);

	useEffect(() => {
		if (!!domainFromRedirect && data && selectedDomains.every((domain) => domain.value !== domainFromRedirect)) {
			const domain = flattenDomains.find((domain) => domain.id === domainFromRedirect);
			if (domain) {
				const savedFilters = isJsonString(data.filters) ? JSON.parse(data.filters || "{}") : {};
				const savedDomains = (savedFilters?.domains || []) as SelectableValue[];
				if (savedDomains.some((d) => d.value === domainFromRedirect)) {
					window.sessionStorage.removeItem("domain");

					return;
				}
				const newDomainsFilters = [...savedDomains, { label: domain.name, value: domain.id }];
				setSelectedDomains(newDomainsFilters);
				updateOrgSettingsFilter({
					variables: {
						req: JSON.stringify({
							usernames: selectedUsers || [],
							tags: selectedTags || [],
							domains: newDomainsFilters,
						}),
					},
				});
				window.sessionStorage.removeItem("domain");
			}
		}
	}, [domainFromRedirect, data, dataDomainsTree]);

	const { organizationContainer } = useOrganizationStyles();

	if (user.role === Role.User) {
		return <NotFound />;
	}

	if (errorUsers || errorTags || errorDomains) {
		return <Typography color="error">Error...{console.error(errorUsers || errorTags || errorDomains)}</Typography>;
	}

	const users = (dataUsers?.GetAllUsersFromOrganization ?? [])
		.map((tempUser: GetAllUsersFromOrganizationQuery["GetAllUsersFromOrganization"][number]) => {
			return { label: (tempUser.lastName ?? "") + " " + (tempUser.firstName ?? ""), value: tempUser.id } as SelectableValue;
		})
		.sort(sortByComparisons(byDesc((i: SelectableValue) => i.label.toLowerCase())));

	const tags = (dataTags?.GetOrganizationAgreementTags ?? [])
		.map((elem: string) => {
			return { label: t(elem), value: elem } as SelectableValue;
		})
		.sort(sortByComparisons(byDesc((i: SelectableValue) => i.label.toLowerCase())));

	const sortedDomains = sortOrganizationDomains(flattenDomains);
	const domains = sortedDomains.map((domain) => ({ label: domain.name, value: domain.id }));

	const handleUsersSelect = (value: any) => {
		setSelectedUsers([...value]);
		updateOrgSettingsFilter({
			variables: {
				req: JSON.stringify({ usernames: value || [], tags: selectedTags ?? [], domains: selectedDomains }),
			},
		});
	};
	const handleTagsSelect = (value: any) => {
		setSelectedTags([...value]);
		updateOrgSettingsFilter({
			variables: {
				req: JSON.stringify({ usernames: selectedUsers || [], tags: value ?? [], domains: selectedDomains || [] }),
			},
		});
	};

	const handleDomainsSelect = (value: any) => {
		setSelectedDomains(sortOrganizationDomainsLabel([...value]));
		updateOrgSettingsFilter({
			variables: {
				req: JSON.stringify({ usernames: selectedUsers || [], tags: selectedTags, domains: value ?? [] }),
			},
		});
	};

	return (
		<Fragment>
			<Box hidden={loading || loadingTags || loadingUsers || loadingDomains}>
				<Card className={organizationContainer}>
					<Box width="100%" paddingTop={5} display="flex" alignItems="flex-start">
						<Box paddingLeft={4}>
							<DropDownMultipleSelection
								id="usernames"
								options={users ?? []}
								index={1}
								values={selectedUsers}
								label={t("username")}
								clearAll={false}
								handleFilter={handleUsersSelect}
								property={"label"}
							/>
						</Box>
						<Box paddingLeft={4}>
							<DropDownMultipleSelection
								id="agreementTags"
								options={tags ?? []}
								index={2}
								values={selectedTags}
								label={t("agreement-tags")}
								handleFilter={handleTagsSelect}
								clearAll={false}
								property={"value"}
							/>
						</Box>
						{props.canAccessDomains && (
							<Box paddingLeft={4}>
								<DropDownMultipleSelection
									id="domains"
									options={[...domains].reverse()}
									index={2}
									values={selectedDomains}
									label={`${t("domain-header-general")}/${t("domain-header-subdomains")}`}
									handleFilter={handleDomainsSelect}
									clearAll={false}
									property="value"
									optionLabel="label"
								/>
							</Box>
						)}
					</Box>
					{dataDomainsTree?.GetUserDomainsTree && (
						<OrganizationActionSettings
							orgId={props.userOrg.id!}
							domainOptions={domains}
							domainOptionsTree={dataDomainsTree.GetUserDomainsTree}
							selectedUsers={selectedUsers}
							selectedTags={selectedTags}
							selectedDomains={selectedDomains}
						/>
					)}
				</Card>
			</Box>

			{(loading || loadingTags || loadingUsers) && (
				<Box width="100%" height="100%" justifyContent="center" alignItems="center" display="flex" padding="20vh">
					<Loading />
				</Box>
			)}
		</Fragment>
	);
};
